import { useEffect, useState } from "react";
import { TeamMembersWithDetailsAndTeam } from "../../types/team_members.types";
import { TeamMembersAPI } from "../../APIs/team_members.api";
import { useCompetitions } from "../../context/CompetitionsContext";
import { CrudTableHeader } from "../../components/crud-table-header";
import CrudTable from "../../components/crud-table";
import { useTableControls } from "../../hooks/useTableControls";
import {
  Gender,
  GenderColors,
  GenderTranslations,
} from "../../types/personal_info.types";
import Label from "../../components/label";
import { UsersIcon } from "@heroicons/react/24/outline";
import { formatPhoneNumber } from "../../utils/format-phone-number.util";
import { calculateAge } from "../../utils/age.utils";
import { BLOOD_TYPE_OPTIONS } from "../../constants/blood-type-select-options";
import { parseBirthdateToLocalFormat } from "../../utils/date.utils";
import { TeamLink } from "../../components/team-link";
import { useErrorHandler } from "../../hooks/useErrorHandler";
import { Loading } from "../../components/loading";
import { ErrorMessage } from "../../components/error-message";

export function TeamMembersAdmins() {
  const [teamMembers, setTeamMembers] = useState<
    TeamMembersWithDetailsAndTeam[]
  >([]);
  const { currentCompetitionId } = useCompetitions();
  const {
    paginatedData: paginatedTeamMembers,
    filteredData,
    totalPages,
    currentPage,
    sortConfig,
    setSortConfig,
    setSearchQuery,
    setSelectedFilters,
    setCurrentPage,
  } = useTableControls<TeamMembersWithDetailsAndTeam>(teamMembers || [], {
    customFilters: {
      "personal_info.birth_date": (birthDateValue, selectedValues) => {
        if (!birthDateValue) return false;
        const age = calculateAge(birthDateValue);

        return selectedValues.some((value) => {
          if (value === "minor") return age < 18;
          if (value === "adult") return age >= 18;
          return false;
        });
      },
    },
  });
  const { error, handleError, clearError } = useErrorHandler();
  const [isLoading, setIsLoading] = useState(true);

  const columns = [
    {
      key: "team_name" as keyof TeamMembersWithDetailsAndTeam,
      label: "Equipo",
      sortable: true,
      render: (value: string, row: TeamMembersWithDetailsAndTeam) => (
        <TeamLink teamName={value} teamId={row.team_id} />
      ),
    },
    {
      key: "username" as keyof TeamMembersWithDetailsAndTeam,
      label: "Usuario",
      sortable: true,
    },
    {
      key: "personal_info.first_name" as keyof TeamMembersWithDetailsAndTeam,
      label: "Nombre",
      sortable: true,
      render: (value: string) => value || "-",
    },
    {
      key: "personal_info.last_name" as keyof TeamMembersWithDetailsAndTeam,
      label: "Apellido",
      sortable: true,
      render: (value: string) => value || "-",
    },
    {
      key: "personal_info.dni" as keyof TeamMembersWithDetailsAndTeam,
      label: "DNI",
      sortable: true,
      render: (value: string) => value || "-",
    },
    {
      key: "personal_info.email" as keyof TeamMembersWithDetailsAndTeam,
      label: "Email",
      sortable: true,
      render: (value: string) => value || "-",
    },
    {
      key: "personal_info.primary_phone_number" as keyof TeamMembersWithDetailsAndTeam,
      label: "Teléfono",
      sortable: true,
      render: (value: string) => (value ? formatPhoneNumber(value) : "-"),
    },

    {
      key: "personal_info.birth_date" as keyof TeamMembersWithDetailsAndTeam,
      label: "Fecha de Nacimiento",
      sortable: true,
      render: (value: string) =>
        value ? parseBirthdateToLocalFormat(value) : "-",
    },
    {
      key: "personal_info.blood_type" as keyof TeamMembersWithDetailsAndTeam,
      label: "Grupo Sanguíneo",
      sortable: true,
      render: (value: string) => value || "-",
    },
    {
      key: "personal_info.gender" as keyof TeamMembersWithDetailsAndTeam,
      label: "Género",
      sortable: true,
      render: (value: Gender) =>
        value ? (
          <Label color={GenderColors[value as Gender]}>
            {GenderTranslations[value]}
          </Label>
        ) : (
          "-"
        ),
    },
  ];

  useEffect(() => {
    if (!currentCompetitionId) {
      return;
    }
    const fetchTeamMembers = async () => {
      setIsLoading(true);
      clearError();
      try {
        const response = await TeamMembersAPI.getAllTeamMembers(
          currentCompetitionId
        );
        if (response.statusCode === 200) {
          setTeamMembers(response.data);
        } else {
          handleError(response.message, { persist: true });
        }
      } catch (err) {
        handleError("Error de conexión, intente nuevamente.", {
          persist: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchTeamMembers();
  }, [currentCompetitionId, handleError, clearError]);

  useEffect(() => {}, [paginatedTeamMembers]);

  const handleSort = (
    sortBy: string | null,
    sortDirection: "asc" | "desc" | null
  ) => {
    setSortConfig({
      sortBy: sortBy as keyof TeamMembersWithDetailsAndTeam | null,
      sortDirection,
    });
  };

  return (
    <div className="p-8">
      <CrudTableHeader
        title="Participantes"
        count={teamMembers?.length}
        icon={<UsersIcon />}
      />
      {error && (
        <div className="mb-4">
          <ErrorMessage message={error} dismissible />
        </div>
      )}
      {isLoading ? (
        <Loading />
      ) : (
        <CrudTable
          data={paginatedTeamMembers}
          columns={columns}
          search={{
            placeholder: "Buscar participantes...",
            onSearch: setSearchQuery,
          }}
          sort={{
            sortBy: sortConfig.sortBy,
            sortDirection: sortConfig.sortDirection,
            onSort: handleSort,
          }}
          pagination={{
            page: currentPage,
            totalPages: totalPages,
            onPageChange: setCurrentPage,
          }}
          filters={[
            {
              key: "personal_info.gender",
              label: "Género",
              multiple: false,
              options: Object.entries(GenderTranslations).map(
                ([key, value]) => ({
                  key: key as Gender,
                  value,
                })
              ),
            },
            {
              key: "personal_info.birth_date",
              label: "Edad",
              multiple: false,
              options: [
                { key: "minor", value: "Menores" },
                { key: "adult", value: "Mayores" },
              ],
            },
            {
              key: "personal_info.blood_type",
              label: "Grupo Sanguíneo",
              multiple: true,
              options: BLOOD_TYPE_OPTIONS,
            },
          ]}
          onFilterChange={setSelectedFilters}
          totalCount={filteredData.length}
        />
      )}
    </div>
  );
}
